import { ChangeDetectionStrategy, Component } from "@angular/core";
import { Observable } from "rxjs";
import { environment } from "src/environments/environment";
import {
  DeviceGateReason,
  DeviceGateService,
  ViewportSize,
} from "./device-gate.service";

/**
 * The "use a bigger screen" screen. Pure presentation — whether it shows at all
 * is DeviceGateService's call, and AppComponent renders this INSTEAD of the
 * router outlet so neither the login nor the Zoom SDK is ever constructed on a
 * phone. The copy switches on the reason the service gives.
 */
@Component({
  selector: "app-device-gate",
  templateUrl: "./device-gate.component.html",
  styleUrls: ["./device-gate.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeviceGateComponent {
  /** "sign-in", "live-class" or "small-window" — see DeviceGateReason. */
  readonly reason$: Observable<DeviceGateReason | null>;

  /** Live window size, and the size it has to reach. Shown as real numbers on
   *  the "make the window bigger" screen: it turns an instruction the student
   *  has to interpret into a target they can watch themselves hit. */
  readonly viewport$: Observable<ViewportSize>;
  readonly minWidth: number;
  readonly minHeight: number;

  /**
   * The address to reopen on a real screen — the canonical CAIRA login, from
   * environment.cairaLoginUrl.
   *
   * NOT window.location: that prints whatever host the browser is on, which on a
   * UAT build, a preview deploy or a dev server is an address the student cannot
   * use. This is something they are going to read off one screen and type into
   * another, so it has to be the real one.
   */
  readonly siteUrl = environment.cairaLoginUrl;

  /**
   * The same address without its scheme — "caira.mileseducation.com/login".
   * Shorter to read and to retype, and `https://` tells the student nothing.
   */
  readonly siteUrlDisplay = (environment.cairaLoginUrl || "").replace(
    /^https?:\/\//,
    "",
  );

  constructor(private deviceGate: DeviceGateService) {
    this.reason$ = this.deviceGate.reason$;
    this.viewport$ = this.deviceGate.viewport$;
    this.minWidth = this.deviceGate.minWidth;
    this.minHeight = this.deviceGate.minHeight;
  }

  /** Which dimension is actually short, so the copy names it instead of making
   *  the student work it out. Both is possible. */
  shortSide(size: ViewportSize): string {
    const narrow = size.width < this.minWidth;
    const short = size.height < this.minHeight;
    if (narrow && short) {
      return "wider and taller";
    }
    return narrow ? "wider" : "taller";
  }
}
